import { useCallback, useEffect, useRef, useState } from 'react';

import type { IntakeFormDefinition, UUID } from '@aline/common';

import { useIntakeFormTemplate } from './use-intake-form-template';

const TITLE_SAVE_DELAY_MS = 500;

export interface IntakeFormBuilderSaveSnapshot {
  definition: IntakeFormDefinition;
  isEmpty: boolean;
  key: string;
  title: string;
}

interface CreateSaveSnapshotInput {
  definition: IntakeFormDefinition;
  fieldCount: number;
  title: string;
}

interface InitializePersistenceInput {
  isAttached: boolean;
  lastSavedSnapshot: IntakeFormBuilderSaveSnapshot | null;
  templateId: UUID | null;
}

export function createIntakeFormBuilderSaveSnapshot({
  definition,
  fieldCount,
  title,
}: CreateSaveSnapshotInput): IntakeFormBuilderSaveSnapshot {
  const trimmedTitle = title.trim();

  return {
    definition,
    isEmpty: !trimmedTitle && fieldCount === 0,
    key: JSON.stringify({ def: definition, title: trimmedTitle }),
    title: trimmedTitle,
  };
}

export function useIntakeFormBuilderPersistence(workflowId: UUID) {
  const savingRef = useRef(false);
  const lastSavedKeyRef = useRef('');
  const attachedRef = useRef(false);
  const currentTemplateIdRef = useRef<UUID | null>(null);
  const { saveIntakeForm } = useIntakeFormTemplate(workflowId);
  const titleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [currentTemplateId, setCurrentTemplateId] = useState<UUID | null>(null);
  const pendingSnapshotRef = useRef<IntakeFormBuilderSaveSnapshot | null>(null);
  const pendingTitleSnapshotRef = useRef<IntakeFormBuilderSaveSnapshot | null>(null);

  const recordTemplateId = useCallback((id: UUID | null) => {
    currentTemplateIdRef.current = id;
    setCurrentTemplateId(id);
  }, []);

  const saveSnapshot = useCallback(
    async (snapshot: IntakeFormBuilderSaveSnapshot): Promise<void> => {
      if (snapshot.key === lastSavedKeyRef.current) return;
      if (!currentTemplateIdRef.current && snapshot.isEmpty) return;

      if (savingRef.current) {
        pendingSnapshotRef.current = snapshot;
        return;
      }

      try {
        savingRef.current = true;
        const id = await saveIntakeForm({
          existingTemplateId: currentTemplateIdRef.current,
          formDefinition: snapshot.definition,
          isAttached: attachedRef.current,
          onTemplateCreated: recordTemplateId,
          title: snapshot.title,
        });
        lastSavedKeyRef.current = snapshot.key;
        attachedRef.current = true;
        recordTemplateId(id);
      } catch {
        // Apollo errors surface through the global notifier.
      } finally {
        savingRef.current = false;
        const pendingSnapshot = pendingSnapshotRef.current;
        pendingSnapshotRef.current = null;
        if (pendingSnapshot) void saveSnapshot(pendingSnapshot);
      }
    },
    [recordTemplateId, saveIntakeForm]
  );

  const clearTitleTimer = useCallback(() => {
    if (!titleTimerRef.current) return;
    clearTimeout(titleTimerRef.current);
    titleTimerRef.current = null;
  }, []);

  const saveNow = useCallback(
    (snapshot: IntakeFormBuilderSaveSnapshot) => {
      clearTitleTimer();
      pendingTitleSnapshotRef.current = null;
      void saveSnapshot(snapshot);
    },
    [clearTitleTimer, saveSnapshot]
  );

  const scheduleTitleSave = useCallback(
    (snapshot: IntakeFormBuilderSaveSnapshot) => {
      clearTitleTimer();
      pendingTitleSnapshotRef.current = snapshot;
      titleTimerRef.current = setTimeout(() => {
        const pendingTitleSnapshot = pendingTitleSnapshotRef.current;
        pendingTitleSnapshotRef.current = null;
        titleTimerRef.current = null;
        if (pendingTitleSnapshot) void saveSnapshot(pendingTitleSnapshot);
      }, TITLE_SAVE_DELAY_MS);
    },
    [clearTitleTimer, saveSnapshot]
  );

  const flushTitleSave = useCallback(() => {
    clearTitleTimer();
    const pendingTitleSnapshot = pendingTitleSnapshotRef.current;
    pendingTitleSnapshotRef.current = null;
    if (pendingTitleSnapshot) void saveSnapshot(pendingTitleSnapshot);
  }, [clearTitleTimer, saveSnapshot]);

  const initialize = useCallback(
    ({ isAttached, lastSavedSnapshot, templateId }: InitializePersistenceInput) => {
      clearTitleTimer();
      attachedRef.current = isAttached;
      lastSavedKeyRef.current = lastSavedSnapshot?.key ?? '';
      pendingSnapshotRef.current = null;
      pendingTitleSnapshotRef.current = null;
      recordTemplateId(templateId);
    },
    [clearTitleTimer, recordTemplateId]
  );

  useEffect(() => flushTitleSave, [flushTitleSave]);

  return {
    currentTemplateId,
    flushTitleSave,
    initialize,
    saveNow,
    scheduleTitleSave,
  };
}
